import React, { useEffect, useState } from 'react';
import BopRowActions, { RowIcon } from '../../components/BopRowActions';
import { apiFetch } from '../../lib/api';

/**
 * Blocco di verifica di un singolo ordine importato.
 *
 * Si corregge ciò che l'estrazione non ha saputo agganciare — l'articolo di una
 * riga, il negozio dell'ordine — e si dichiara l'ordine pronto per GO.
 * Quantità e prezzi non sono modificabili: sono ciò che il cliente ha ordinato.
 */

export type MatchNegozio = 'FILIALE' | 'CLIENTE' | 'NESSUNO';
export type StatoOrdine = 'DA_VERIFICARE' | 'VERIFICATO' | 'INVIATO_GO' | 'ERRORE_GO' | 'SCARTATO';

export interface RigaOrdine {
  id: number;
  numeroRiga: number;
  ean: string | null;
  codiceFornitore: string | null;
  codiceCliente: string | null;
  descrizione: string | null;
  quantita: string | number;
  prezzoUnitario: string | number | null;
  articoloId: number | null;
  matchTipo: string;
  scartata: boolean;
  motivoScarto: string | null;
  articolo: { id: number; codice: string; descrizioneBase: string } | null;
}

export interface OrdineEstratto {
  id: number;
  numeroOrdine: string;
  codiceNegozio: string;
  nomeNegozio: string | null;
  indirizzoNegozio: string | null;
  capNegozio: string | null;
  cittaNegozio: string | null;
  nomeDeposito: string | null;
  dataConsegnaDa: string | null;
  totaleDichiarato: string | number | null;
  note: string | null;
  matchNegozio: MatchNegozio;
  stato: StatoOrdine;
  righeTotali: number;
  righeNonRisolte: number;
  righe: RigaOrdine[];
  cliente: { id: number; ragioneSociale: string | null } | null;
  filiale: { id: number; nome: string } | null;
  // esito dell'invio a GO: progressivo o messaggio d'errore
  esitoInvio: string | null;
  goId: string | null;
  inviatoAt: string | null;
  // numero d'ordine che GO aveva già: valorizzato solo quando l'invio ha
  // dovuto ripiegare su un riferimento suffissato (goNumero tiene quello usato)
  goRiferimentoOccupato: string | null;
  goNumero: string | null;
  // cosa GO ha registrato, riletto dopo l'invio
  goNumeroDocumento: string | null;
  goRighe: number | null;
  goImponibile: string | number | null;
  goTotale: string | number | null;
  goSituazione: string | null;
  goRilevatoAt: string | null;
}

/** Un ordine di BOP che porta lo stesso numero: da dove l'avevamo già visto. */
export interface OccupatoInBop {
  id: number;
  importId: number;
  numeroOrdine: string;
  codiceNegozio: string;
  nomeNegozio: string | null;
  dataOrdine: string | null;
  stato: StatoOrdine;
  goId: string | null;
  goNumero: string | null;
  goNumeroDocumento: string | null;
  goRighe: number | null;
  goTotale: string | number | null;
  inviatoAt: string | null;
  cliente: { id: number; ragioneSociale: string | null } | null;
  filiale: { id: number; nome: string } | null;
  importOrdini: { id: number; etichetta: string; createdAt: string } | null;
}

/** Una testata di GO che porta lo stesso numero, letta dal database di GO. */
export interface OccupatoInGo {
  progressivo: string;
  riferimento: string | null;
  numeroDocumento: string | null;
  tipoDocumento: string | null;
  dataDocumento: string | null;
  dataRiferimento: string | null;
  dataConsegna: string | null;
  situazione: string | null;
  codiceCliente: string | null;
  cliente: string | null;
  codiceDestinazione: string | null;
  destinazione: string | null;
  imponibile: number | null;
  totale: number | null;
}

export interface RecapOccupato {
  riferimentoOccupato: string;
  riferimentoUsato: string | null;
  inBop: OccupatoInBop[];
  inGo: OccupatoInGo[];
  // Il database di GO non era interrogabile: si mostra comunque la parte BOP.
  erroreGo: string | null;
}

export interface ArticoloOpzione {
  id: number;
  codice: string;
  descrizioneBase: string;
  ean: string | null;
}

export interface NegozioOpzione {
  id: number;
  codice: string;
  ragioneSociale: string | null;
  filiali: { id: number; nome: string; citta: string | null }[];
}

function fmtData(iso?: string | null): string {
  if (!iso) return '—';
  // Le date che arrivano dalla vista di GO sono stringhe "YYYY-MM-DD ..." senza
  // fuso: new Date le legge comunque, e senza ora non c'è sfasamento da fuso.
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('it-IT');
}

function fmtDataOra(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('it-IT');
}

function fmtImporto(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
}

const ETICHETTA_MATCH: Record<string, string> = {
  CODICE_INTERNO: 'codice Smartix',
  EAN: 'EAN',
  CODICE_CLIENTE: 'codice cliente',
  CODICE_INSEGNA: 'codice insegna',
  MANUALE: 'scelto a mano',
  NESSUNO: '—',
};

interface Props {
  importId: number | string;
  ordine: OrdineEstratto;
  articoli: ArticoloOpzione[];
  negozi: NegozioOpzione[];
  modificabile: boolean;
  onAggiornato: () => void;
}

export default function VerificaOrdineGo({ importId, ordine, articoli, negozi, modificabile, onAggiornato }: Props) {
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [inCorso, setInCorso] = useState(false);

  // Recap del numero d'ordine che GO aveva già: caricato solo per gli ordini
  // entrati con un suffisso, che sono pochissimi.
  const [occupato, setOccupato] = useState<RecapOccupato | null>(null);
  const [occupatoInCorso, setOccupatoInCorso] = useState(false);

  // selezioni locali per le righe da risolvere
  const [scelte, setScelte] = useState<Record<number, string>>({});
  const [memorizza, setMemorizza] = useState<Record<number, boolean>>({});

  // selezione negozio
  const [clienteScelto, setClienteScelto] = useState<string>(ordine.cliente ? String(ordine.cliente.id) : '');
  const [filialeScelta, setFilialeScelta] = useState<string>(ordine.filiale ? String(ordine.filiale.id) : '');
  // Alcuni tracciati portano solo il codice del negozio: senza un nome la
  // filiale creata sarebbe illeggibile, quindi è digitabile.
  const [nomeFiliale, setNomeFiliale] = useState<string>(ordine.nomeNegozio || '');

  const riferimentoOccupato = ordine.goRiferimentoOccupato;
  useEffect(() => {
    if (!riferimentoOccupato) { setOccupato(null); return; }
    let vivo = true;
    setOccupatoInCorso(true);
    // @api GET /api/bop-go/import-ordini/:id/ordini/:ordineId/riferimento-occupato -> {"success":true,"data":{"riferimentoOccupato":"4512345","riferimentoUsato":"4512345-1","inBop":[],"inGo":[{"progressivo":"40625","numeroDocumento":"SM 2318","cliente":"LEROY MERLIN ITALIA","righe":4,"totale":812.5}],"erroreGo":null}}
    apiFetch<{ success: boolean; data: RecapOccupato | null }>(
      `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/riferimento-occupato`
    )
      .then(res => { if (vivo) setOccupato(res.data); })
      // Il recap è un di più: se non arriva resta l'avviso, che è la parte
      // che non deve mancare mai.
      .catch(() => { if (vivo) setOccupato(null); })
      .finally(() => { if (vivo) setOccupatoInCorso(false); });
    return () => { vivo = false; };
  }, [importId, ordine.id, riferimentoOccupato]);

  const bloccato = !modificabile || ordine.stato === 'INVIATO_GO';
  const righeVive = ordine.righe.filter(r => !r.scartata);
  const righeAperte = righeVive.filter(r => !r.articoloId);
  const completo = ordine.matchNegozio !== 'NESSUNO' && righeAperte.length === 0 && righeVive.length > 0;

  async function reinviaGo() {
    if (!window.confirm(`Rimandare a GO l'ordine ${ordine.numeroOrdine}?`)) return;
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    try {
      // @api POST /api/bop-go/import-ordini/:id/ordini/:ordineId/reinvia-go -> {"success":true,"data":{"ok":true,"progressivo":"40694","riferimento":"4512345","riferimentoOccupato":null}}
      const res = await apiFetch<{
        success: boolean;
        data: { ok: boolean; progressivo?: string; errore?: string; riferimento?: string; riferimentoOccupato?: string | null };
      }>(
        `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/reinvia-go`,
        { method: 'POST' }
      );
      if (res.data.ok) {
        // Se il numero era occupato l'ordine in GO si chiama diversamente: va
        // detto subito, non solo al ricaricamento della pagina.
        setMessaggio(res.data.riferimentoOccupato
          ? `Inviato a GO: progressivo ${res.data.progressivo}. Il numero ${res.data.riferimentoOccupato} era già presente in GO: inserito come ${res.data.riferimento}.`
          : `Inviato a GO: progressivo ${res.data.progressivo}`);
      } else setErrore(`Invio fallito di nuovo: ${res.data.errore}`);
      onAggiornato();
    } catch (err: any) {
      setErrore(err.message || 'Reinvio non riuscito');
    } finally {
      setInCorso(false);
    }
  }

  async function chiama(url: string, body: unknown, descrizione: string) {
    setInCorso(true);
    setErrore('');
    setMessaggio('');
    try {
      const res = await apiFetch<{ success: boolean; memorizzato?: string | null }>(url, {
        method: 'PUT', body: JSON.stringify(body),
      });
      setMessaggio(res.memorizzato || descrizione);
      onAggiornato();
    } catch (err: any) {
      setErrore(err.message || 'Operazione non riuscita');
    } finally {
      setInCorso(false);
    }
  }

  function assegnaArticolo(riga: RigaOrdine) {
    const scelto = scelte[riga.id];
    if (!scelto) { setErrore(`Riga ${riga.numeroRiga}: seleziona un articolo`); return; }
    // @api PUT /api/bop-go/import-ordini/:id/ordini/:ordineId/righe/:rigaId {"articoloId":48,"memorizzaCodice":true} -> {"success":true,"data":{"id":1,"articoloId":48,"matchTipo":"MANUALE"},"memorizzato":"codice 410007660 memorizzato per l'insegna"}
    void chiama(
      `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/righe/${riga.id}`,
      { articoloId: Number(scelto), memorizzaCodice: !!memorizza[riga.id] },
      `Riga ${riga.numeroRiga} agganciata`
    );
  }

  function scartaRiga(riga: RigaOrdine) {
    const motivo = riga.scartata ? null : window.prompt(`Motivo dello scarto della riga ${riga.numeroRiga}:`, '');
    if (!riga.scartata && motivo === null) return;
    void chiama(
      `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/righe/${riga.id}`,
      { scartata: !riga.scartata, motivoScarto: motivo },
      riga.scartata ? `Riga ${riga.numeroRiga} ripristinata` : `Riga ${riga.numeroRiga} scartata`
    );
  }

  function associaNegozio(creaFiliale: boolean) {
    if (!clienteScelto) { setErrore('Seleziona il cliente'); return; }
    // @api PUT /api/bop-go/import-ordini/:id/ordini/:ordineId/negozio {"clienteId":21,"filialeId":3,"creaFiliale":false} -> {"success":true,"data":{"id":1,"clienteId":21,"filialeId":3,"matchNegozio":"FILIALE"},"filialeCreata":null}
    void chiama(
      `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/negozio`,
      { clienteId: Number(clienteScelto), filialeId: filialeScelta ? Number(filialeScelta) : null, creaFiliale, nomeFiliale: nomeFiliale.trim() },
      creaFiliale ? 'Filiale creata e associata' : 'Negozio associato'
    );
  }

  function cambiaStato(stato: StatoOrdine) {
    const motivo = stato === 'SCARTATO' ? window.prompt("Motivo dello scarto dell'ordine:", '') : null;
    if (stato === 'SCARTATO' && motivo === null) return;
    // @api PUT /api/bop-go/import-ordini/:id/ordini/:ordineId/stato {"stato":"VERIFICATO"} -> {"success":true,"data":{"id":1,"stato":"VERIFICATO"}}
    void chiama(
      `/api/bop-go/import-ordini/${importId}/ordini/${ordine.id}/stato`,
      { stato, motivo },
      stato === 'VERIFICATO' ? 'Ordine verificato' : stato === 'SCARTATO' ? 'Ordine scartato' : 'Ordine riaperto'
    );
  }

  const filialiCliente = negozi.find(n => String(n.id) === clienteScelto)?.filiali || [];

  return (
    <div className="bop-go-verifica">
      {ordine.note && (
        <p className="bop-text-muted"><strong>Nota dal file:</strong> {ordine.note}</p>
      )}

      {/* Il numero d'ordine del file era già speso in GO: l'ordine è entrato
          lo stesso, ma con un altro riferimento. Va detto prima di tutto il
          resto, perché chi cerca l'ordine in GO cerca il numero del file. */}
      {ordine.goRiferimentoOccupato && (
        <div className="bop-go-occupato">
          <div className="bop-alert bop-alert--warning">
            <strong>Numero già presente in GO.</strong>{' '}
            Il numero d'ordine <span className="bop-text-mono">{ordine.goRiferimentoOccupato}</span>{' '}
            risultava già occupato: questo ordine è stato registrato con riferimento{' '}
            <span className="bop-text-mono"><strong>{ordine.goNumero || '—'}</strong></span>.
          </div>

          {occupatoInCorso && <p className="bop-text-muted">Cerco cosa occupava quel numero…</p>}

          {occupato && (occupato.inGo.length > 0 || occupato.inBop.length > 0 || occupato.erroreGo) && (
            <div className="bop-card">
              <div className="bop-card__header">
                <h4 className="bop-card__title">Cosa occupava il numero {occupato.riferimentoOccupato}</h4>
              </div>
              <div className="bop-card__body">
                {occupato.inGo.length > 0 && (
                  <>
                    <p className="bop-go-occupato__fonte">Documenti in GO</p>
                    <div className="bop-table-wrapper">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th className="bop-table__th">Progressivo</th>
                            <th className="bop-table__th">Documento</th>
                            <th className="bop-table__th">Data</th>
                            <th className="bop-table__th">Riferimento</th>
                            <th className="bop-table__th">Cliente</th>
                            <th className="bop-table__th">Destinazione</th>
                            <th className="bop-table__th bop-text-right">Totale</th>
                            <th className="bop-table__th">Situazione</th>
                          </tr>
                        </thead>
                        <tbody>
                          {occupato.inGo.map(d => (
                            <tr className="bop-table__row" key={d.progressivo}>
                              <td className="bop-table__td bop-text-mono">{d.progressivo}</td>
                              <td className="bop-table__td">{d.numeroDocumento || '—'}</td>
                              <td className="bop-table__td">{fmtData(d.dataDocumento)}</td>
                              <td className="bop-table__td bop-text-mono">{d.riferimento || '—'}</td>
                              <td className="bop-table__td">{d.cliente || d.codiceCliente || '—'}</td>
                              <td className="bop-table__td">{d.destinazione || d.codiceDestinazione || '—'}</td>
                              <td className="bop-table__td bop-text-right">{fmtImporto(d.totale)}</td>
                              <td className="bop-table__td">{d.situazione || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {occupato.inBop.length > 0 && (
                  <>
                    <p className="bop-go-occupato__fonte">Ordini con lo stesso numero già passati da BOP</p>
                    <div className="bop-table-wrapper">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th className="bop-table__th">Numero</th>
                            <th className="bop-table__th">Negozio</th>
                            <th className="bop-table__th">Cliente</th>
                            <th className="bop-table__th">Data ordine</th>
                            <th className="bop-table__th">Caricamento</th>
                            <th className="bop-table__th">Inviato</th>
                            <th className="bop-table__th">In GO</th>
                          </tr>
                        </thead>
                        <tbody>
                          {occupato.inBop.map(o => (
                            <tr className="bop-table__row" key={o.id}>
                              <td className="bop-table__td bop-text-mono">{o.goNumero || o.numeroOrdine}</td>
                              <td className="bop-table__td">
                                {o.codiceNegozio}{o.nomeNegozio ? ` — ${o.nomeNegozio}` : ''}
                              </td>
                              <td className="bop-table__td">
                                {o.cliente?.ragioneSociale || '—'}
                                {o.filiale ? ` / ${o.filiale.nome}` : ''}
                              </td>
                              <td className="bop-table__td">{fmtData(o.dataOrdine)}</td>
                              <td className="bop-table__td">{o.importOrdini?.etichetta || `#${o.importId}`}</td>
                              <td className="bop-table__td">{fmtDataOra(o.inviatoAt)}</td>
                              <td className="bop-table__td">
                                {o.goId
                                  ? `progressivo ${o.goId}${o.goNumeroDocumento ? ` · ${o.goNumeroDocumento}` : ''}`
                                  : o.stato}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {occupato.erroreGo && (
                  <p className="bop-text-muted">
                    I documenti di GO non sono stati letti: {occupato.erroreGo}
                  </p>
                )}
              </div>
            </div>
          )}

          {occupato && !occupatoInCorso && occupato.inGo.length === 0
            && occupato.inBop.length === 0 && !occupato.erroreGo && (
            <p className="bop-text-muted">
              GO ha rifiutato il numero come già presente, ma il documento che lo occupa
              non è stato trovato né in BOP né nell'archivio ordini di GO: potrebbe essere
              un documento di tipo diverso dall'ordine di vendita.
            </p>
          )}
        </div>
      )}

      {ordine.stato === 'INVIATO_GO' && (
        <p className="bop-text-muted">
          <strong>Inviato a GO:</strong> {ordine.esitoInvio || `progressivo ${ordine.goId}`}
          {ordine.goNumeroDocumento && ` · documento ${ordine.goNumeroDocumento}`}
          {ordine.goSituazione && ` (${ordine.goSituazione})`}
          {ordine.inviatoAt && ` · ${new Date(ordine.inviatoAt).toLocaleString('it-IT')}`}
        </p>
      )}
      {ordine.stato === 'ERRORE_GO' && (
        <p className="bop-text-danger">
          <strong>Invio a GO fallito:</strong> {ordine.esitoInvio || 'motivo non registrato'} — correggi e riapri l'ordine per rimandarlo.
        </p>
      )}

      {/* ── Negozio ─────────────────────────────────────────────────── */}
      <div className="bop-card">
        <div className="bop-card__header">
          <h4 className="bop-card__title">Negozio che ordina</h4>
        </div>
        <div className="bop-card__body">
          <p className="bop-text-muted">
            Dal file: <strong>{ordine.codiceNegozio}</strong> — {ordine.nomeNegozio || 'senza nome'}
            {ordine.indirizzoNegozio && `, ${ordine.indirizzoNegozio}`}
            {ordine.capNegozio && ` ${ordine.capNegozio}`}
            {ordine.cittaNegozio && ` ${ordine.cittaNegozio}`}
          </p>

          {ordine.matchNegozio !== 'NESSUNO' && (
            <p>
              Associato a{' '}
              <span className="bop-badge bop-badge-success">
                {ordine.filiale ? `${ordine.cliente?.ragioneSociale} / ${ordine.filiale.nome}` : ordine.cliente?.ragioneSociale}
              </span>
            </p>
          )}

          {!bloccato && (
            <div className="bop-filter-bar">
              <select
                className="bop-select"
                value={clienteScelto}
                onChange={(e) => { setClienteScelto(e.target.value); setFilialeScelta(''); setErrore(''); }}
                disabled={inCorso}
              >
                <option value="">— cliente dell'insegna —</option>
                {negozi.map(n => (
                  <option key={n.id} value={n.id}>{n.ragioneSociale || n.codice}</option>
                ))}
              </select>
              <select
                className="bop-select"
                value={filialeScelta}
                onChange={(e) => setFilialeScelta(e.target.value)}
                disabled={inCorso || !clienteScelto}
              >
                <option value="">— nessuna filiale —</option>
                {filialiCliente.map(f => (
                  <option key={f.id} value={f.id}>{f.nome}{f.citta ? ` (${f.citta})` : ''}</option>
                ))}
              </select>
              <button className="bop-btn bop-btn-primary" onClick={() => associaNegozio(false)} disabled={inCorso}>
                Associa
              </button>
              <input
                className="bop-input"
                value={nomeFiliale}
                onChange={(e) => { setNomeFiliale(e.target.value); setErrore(''); }}
                placeholder={ordine.nomeNegozio ? 'Nome della nuova filiale' : `es. Negozio ${ordine.codiceNegozio}`}
                disabled={inCorso}
                title="Nome della filiale da creare: proposto dal file quando c'è, altrimenti da scrivere"
              />
              <button
                className="bop-btn bop-btn-secondary"
                onClick={() => associaNegozio(true)}
                disabled={inCorso || !clienteScelto || !nomeFiliale.trim()}
                title="Crea la filiale con indirizzo, CAP e città letti dal file"
              >
                Crea filiale
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Conferma: subito sotto il negozio, dove si decide ─────────── */}
      {errore && <p className="bop-text-danger">{errore}</p>}
      {messaggio && <p className="bop-text-muted">{messaggio}</p>}

      {!bloccato && (
        <div className="bop-flex">
          {ordine.stato === 'DA_VERIFICARE' && (
            <>
              <button
                className="bop-btn bop-btn-primary"
                onClick={() => cambiaStato('VERIFICATO')}
                disabled={inCorso || !completo}
                title={completo ? 'Dichiara questo ordine pronto per GO' : 'Risolvi prima negozio e righe'}
              >
                Conferma ordine
              </button>
              <button className="bop-btn bop-btn-danger" onClick={() => cambiaStato('SCARTATO')} disabled={inCorso}>
                Scarta ordine
              </button>
              {!completo && (
                <span className="bop-text-muted">
                  {ordine.matchNegozio === 'NESSUNO' && 'negozio da associare'}
                  {ordine.matchNegozio === 'NESSUNO' && righeAperte.length > 0 && ' · '}
                  {righeAperte.length > 0 && `${righeAperte.length} righe senza articolo`}
                  {righeVive.length === 0 && 'tutte le righe sono scartate'}
                </span>
              )}
            </>
          )}
          {ordine.stato === 'ERRORE_GO' && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={reinviaGo}
              disabled={inCorso || !completo}
              title={completo ? "Rimanda a GO l'ordine così com'è" : 'Risolvi prima negozio e righe'}
            >
              {inCorso ? 'Invio…' : 'Reinvia a GO'}
            </button>
          )}
          {(ordine.stato === 'VERIFICATO' || ordine.stato === 'SCARTATO' || ordine.stato === 'ERRORE_GO') && (
            <button className="bop-btn bop-btn-secondary" onClick={() => cambiaStato('DA_VERIFICARE')} disabled={inCorso}>
              Riapri ordine
            </button>
          )}
        </div>
      )}

      {/* ── Righe ───────────────────────────────────────────────────── */}
      <table className="bop-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Codice Smartix</th>
            <th>Codice cliente</th>
            <th>EAN</th>
            <th>Descrizione</th>
            <th className="bop-text-right">Qtà</th>
            <th className="bop-text-right">Prezzo</th>
            <th>Articolo BOP</th>
            <th className="bop-text-right">Azioni</th>
          </tr>
        </thead>
        <tbody>
          {ordine.righe.map(r => (
            <tr key={r.id} className={r.scartata ? 'bop-text-muted' : undefined}>
              <td>{r.numeroRiga}</td>
              <td>{r.codiceFornitore || '—'}</td>
              <td>{r.codiceCliente || '—'}</td>
              <td>{r.ean || '—'}</td>
              <td>{r.descrizione || '—'}</td>
              <td className="bop-text-right">{Number(r.quantita)}</td>
              <td className="bop-text-right">{r.prezzoUnitario != null ? Number(r.prezzoUnitario).toFixed(2) : '—'}</td>
              <td>
                {r.scartata ? (
                  <span className="bop-badge bop-badge-neutral">
                    scartata{r.motivoScarto ? `: ${r.motivoScarto}` : ''}
                  </span>
                ) : r.articolo ? (
                  <>
                    <span className="bop-badge bop-badge-success">{r.articolo.codice}</span>{' '}
                    <span className="bop-text-muted">{ETICHETTA_MATCH[r.matchTipo] || r.matchTipo}</span>
                  </>
                ) : bloccato ? (
                  <span className="bop-badge bop-badge-warning">non trovato</span>
                ) : (
                  <div className="bop-flex">
                    <select
                      className="bop-select"
                      value={scelte[r.id] || ''}
                      onChange={(e) => { setScelte({ ...scelte, [r.id]: e.target.value }); setErrore(''); }}
                      disabled={inCorso}
                    >
                      <option value="">— scegli l'articolo —</option>
                      {articoli.map(a => (
                        <option key={a.id} value={a.id}>{a.codice} — {a.descrizioneBase}</option>
                      ))}
                    </select>
                    <label className="bop-flex" title="Vale per tutti i negozi dell'insegna: al prossimo import questa riga si aggancia da sola">
                      <input
                        type="checkbox"
                        checked={memorizza[r.id] !== false}
                        onChange={(e) => setMemorizza({ ...memorizza, [r.id]: e.target.checked })}
                        disabled={inCorso || !r.codiceCliente}
                      />
                      <span className="bop-text-muted">memorizza</span>
                    </label>
                    <button className="bop-btn bop-btn-primary bop-btn-sm" onClick={() => assegnaArticolo(r)} disabled={inCorso}>
                      Assegna
                    </button>
                  </div>
                )}
              </td>
              <td className="bop-text-right">
                {!bloccato && (
                  <BopRowActions
                    actions={[
                      {
                        icon: r.scartata ? <RowIcon.Copy /> : <RowIcon.Disable />,
                        label: r.scartata ? 'Ripristina riga' : 'Scarta riga',
                        onClick: () => scartaRiga(r),
                        variant: r.scartata ? 'default' : 'danger',
                        disabled: inCorso,
                      },
                    ]}
                  />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

    </div>
  );
}
